import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import ModalHandlerComponent from '../Modal/ModalHandlerComponent';

import { getClipTiming, getEEDownloadDurationOptions } from './utils';

import { CUSTOM } from '../Modal/modalTypes';
import StyledSelect from '../Select';
import DateTimeFnsPicker from './DateTimeFnsPicker';

const VideoDownloadComponent = (props) => {
  const { t } = useTransl();
  const {
    setDownloadData,
    startDate,
    onSetStartDate,
    duration,
    onDurationChange,
    isLoading,
    videoList,
    activeDownloads,
    getVideoDownloadTitle,
    downloadVideoClip,
    hasNewParams,
    getVideoList,
    setHasNewParams,
  } = props;

  return (
    <ModalHandlerComponent
      modal={{
        modalType: CUSTOM,
        modalProps: { title: t('camera.videoDownloader') },
      }}
      onModalClose={() => setDownloadData({})}
    >
      <Box>
        <Box display="flex" flexWrap="wrap" gap="1rem">
          <Box>
            <Typography fontWeight="bold">
              {t('camera.startTimestamp')}
            </Typography>
            <DateTimeFnsPicker value={startDate} onChange={onSetStartDate} />
          </Box>
          <Box>
            <Typography fontWeight="bold">{t('camera.duration')}</Typography>
            <Box width="20rem">
              <StyledSelect
                value={duration}
                onChange={onDurationChange}
                options={getEEDownloadDurationOptions()}
                placeholder={t('camera.chooseDuration')}
                sortAlphabetically={false}
                disableClearable
              />
            </Box>
          </Box>
        </Box>
        <Box height="30rem" overflow="auto" my="2rem">
          {isLoading && <CircularLoaderComponent />}
          {videoList.length > 0 && !isLoading && (
            <Box>
              {videoList.map((videoTime, i) => {
                const { startTime, duration } = getClipTiming(videoTime);
                const isClipDownloadInProgress = activeDownloads.includes(i);
                const title = getVideoDownloadTitle(startTime, duration);
                return (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    my="1rem"
                    key={videoTime?.s}
                  >
                    <Box display="flex" alignItems="center" flexWrap="wrap">
                      <Box mr="1rem">
                        {t('camera.clip')} {i + 1} — {startTime}
                      </Box>
                      <Box>({duration})</Box>
                    </Box>
                    <Button
                      color="primary"
                      onClick={() => downloadVideoClip(title, videoTime, i)}
                      disabled={Boolean(activeDownloads.length)}
                      sx={{ minWidth: '10rem' }}
                    >
                      {isClipDownloadInProgress
                        ? t('camera.downloading')
                        : t('actions.download')}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
          {videoList.length === 0 && !isLoading && (
            <Box display="flex" justifyContent="center" m="5rem">
              {t('camera.noVideo')}
            </Box>
          )}
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="contained"
            disableElevation
            color="inherit"
            onClick={() => setDownloadData({})}
          >
            {t('actions.close')}
          </Button>
          {hasNewParams && (
            <Button
              variant="contained"
              disableElevation
              color="primary"
              onClick={() => {
                getVideoList();
                setHasNewParams(false);
              }}
            >
              {t('camera.getVideoList')}
            </Button>
          )}
        </Box>
      </Box>
    </ModalHandlerComponent>
  );
};

VideoDownloadComponent.defaultProps = {
  videoList: [],
  activeDownloads: [],
};

VideoDownloadComponent.propTypes = {
  setDownloadData: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(Object).isRequired,
  onSetStartDate: PropTypes.func.isRequired,
  duration: PropTypes.instanceOf(Object).isRequired,
  onDurationChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  videoList: PropTypes.instanceOf(Array),
  activeDownloads: PropTypes.instanceOf(Array),
  getVideoDownloadTitle: PropTypes.func.isRequired,
  downloadVideoClip: PropTypes.func.isRequired,
  hasNewParams: PropTypes.bool.isRequired,
  getVideoList: PropTypes.func.isRequired,
  setHasNewParams: PropTypes.func.isRequired,
};

export default VideoDownloadComponent;
